import _ from "lodash";
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classes from "./AddStudentsModal.scss";
import I18nHOC from "UIComponents/I18nHOC";
import StudentSelection from "../StudentSelection";
import {
  Button,
  EmptyState,
  IconButton,
  SearchInput,
} from "@toddle-edu/ds-web";
import { CloseOutlined } from "@toddle-edu/ds-icons";
import { AddStudentIllustration } from "@toddle-edu/ds-theme";
import classNames from "classnames";
import { isGlobalHeaderOnMultipleScreensEnabled } from "Utils";

const styles = {
  subtitleStyle: {
    maxWidth: "432px",
  },
};

const AddStudentsModal = React.memo(
  ({
    students,
    onClickCancel,
    onClickAdd,
    courseId,
    disableUpdateOnEmpty,
    t,
    portalType,
    mode,
    assignmentId,
    assignmentTags,
    totalStudents,
    isDraft,
    isCurrentCourse,
    testId,
  }) => {
    const [studentsLocally, changeStudentsLocally] = useState([]);
    const [searchTerm, changeSearchTerm] = useState("");
    const isGlobalHeaderEnabled = isGlobalHeaderOnMultipleScreensEnabled();

    useEffect(() => {
      // if draft assignment contains student(s) who are not part of course anymore then do not show them in the student list
      if (isDraft && _.size(students) > _.size(totalStudents)) {
        changeStudentsLocally(_.intersectionBy(students, totalStudents, "id"));
      } else if (_.isEmpty(students)) {
        changeStudentsLocally(totalStudents);
      } else {
        changeStudentsLocally(students);
      }
    }, [students, totalStudents]);

    const onClickAddStudents = () => {
      onClickAdd(studentsLocally);
      onClickCancel();
    };

    // do not disable select button in draft assignment because there can be 0 assigned students
    const disableUpdate =
      !isDraft && disableUpdateOnEmpty && _.isEmpty(studentsLocally);

    const renderBody = () => {
      if (_.isEmpty(students) && _.isEmpty(totalStudents)) {
        return (
          <EmptyState
            illustration={AddStudentIllustration}
            title={t("common:no_students_added_to_this_class")}
            subtitle={t("classRoom:add_students_empty_state_msg")}
            subtitleStyle={styles.subtitleStyle}
          />
        );
      }

      // classes
      const selectedCountClass = classNames(
        "text-label-3",
        classes.selectedCount,
        {
          [classes.selectedCountDisabled]: _.isEmpty(studentsLocally),
        }
      );

      return (
        <>
          <div className={classes.modalBody}>
            <div className={classes.searchContainer}>
              <SearchInput
                placeholder={`${t("common:search")}..`}
                testId={`${testId}-addStudentsModal`}
                value={searchTerm}
                size={"large"}
                onChange={changeSearchTerm}
                dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
                focusSearchBySlash={true}
              />
            </div>
            <StudentSelection
              testId={`${testId}-addStudentsModal`}
              selectedItemType={`OBJECT`}
              includedStudents={studentsLocally}
              updateStudentArray={changeStudentsLocally}
              parentType={`Course`}
              parentId={courseId}
              searchTerm={searchTerm}
              portalType={portalType}
              mode={mode}
              assignmentId={assignmentId}
              assignmentTags={assignmentTags}
              isCurrentCourse={isCurrentCourse}
              isDraft={isDraft}
            />
          </div>
          <div className={classes.footerCon}>
            <div className={selectedCountClass}>
              {t("common:count_selected", {
                count: _.get(studentsLocally, "length", 0),
              })}
            </div>
            <div className={classes.footerBtnCon}>
              <Button
                size="medium"
                style={{ marginRight: "16px" }}
                onClick={onClickCancel}
                testId={`${testId}-addStudentsModal-cancel`}
                dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
                _legacy_variant={"outlined"}
                variant={"neutral"}
                type="outlined"
              >
                {t("common:cancel")}
              </Button>
              <Button
                size="medium"
                variant="progressive"
                onClick={onClickAddStudents}
                testId={`${testId}-addStudentsModal-select`}
                disabled={disableUpdate}
                type="fill"
                dsVersion={isGlobalHeaderEnabled ? "2.0" : "1.0"}
                shortcut={{ code: "Enter", shiftKey: true }}
              >
                {t("common:select")}
              </Button>
            </div>
          </div>
        </>
      );
    };

    return (
      <div className={classes.container}>
        <div className={classes.headerCon}>
          <div className={classes.addStudentsLabel}>
            {t("common:select_students")}
          </div>
          <IconButton
            variant={"neutral"}
            _legacy_variant={"on"}
            type={"plain"}
            size={"medium"}
            testId={`${testId}-addStudentsModal-close`}
            icon={<CloseOutlined variant={"subtle"} />}
            onClick={onClickCancel}
          />
        </div>
        {renderBody()}
      </div>
    );
  }
);

AddStudentsModal.displayName = "AddStudentsModal";

AddStudentsModal.propTypes = {
  disableUpdateOnEmpty: PropTypes.bool,
  isCurrentCourse: PropTypes.bool,
  testId: PropTypes.string,
};

AddStudentsModal.defaultProps = {
  disableUpdateOnEmpty: false,
  isCurrentCourse: false,
  testId: "classRoom-addAssignment",
};

export default I18nHOC({ resource: ["common"] })(AddStudentsModal);
